import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Upload, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/Bits";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;
// Central team only (+ Admin to publish). Studios, CRM, Brand Consultant, Investor excluded.
export const APK_ROLES = [
  "network_admin", "ceo", "central_marketing",
  "sales_manager_products", "accountant", "central_sales_team",
];
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export default function AppUpdate() {
  const { user } = useAuth();
  const isAdmin = user?.role === "network_admin";
  const allowed = APK_ROLES.includes(user?.role);
  const [latest, setLatest] = useState(null);
  const [releases, setReleases] = useState([]);
  const [version, setVersion] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/apk/latest").then(({ data }) => setLatest(data)).catch(() => {});
    if (isAdmin) api.get("/apk/releases").then(({ data }) => setReleases(data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const download = (rel) => {
    if (rel) localStorage.setItem("apk_seen_version", rel.version);
    window.location.href = `${API}${rel.download_url}`;
  };

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose an .apk file");
    if (!version.trim()) return toast.error("Enter a version number");
    const fd = new FormData();
    fd.append("file", f);
    fd.append("version", version.trim());
    fd.append("release_note", note.trim());
    setBusy(true);
    try {
      await api.post("/apk/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Version ${version.trim()} published`);
      setVersion("");
      setNote("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) return <Navigate to="/" replace />;

  return (
    <div data-testid="app-update-page">
      <PageHead title="Veloskin App" subtitle="Download the latest Android app build" />

      {latest ? (
        <Card className="mb-6 border-brand bg-brand-tint/20 shadow-none" data-testid="apk-latest-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Smartphone className="h-4 w-4 text-brand" /> Latest version · {latest.version}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {latest.release_note && (
              <p className="whitespace-pre-wrap text-sm text-brand-ink">{latest.release_note}</p>
            )}
            <p className="text-xs text-slate-500">
              {fmtSize(latest.size)} · published {(latest.created_at || "").slice(0, 10)}
              {latest.uploaded_by ? ` by ${latest.uploaded_by}` : ""}
            </p>
            <Button className="min-h-[44px]" data-testid="apk-download-btn" onClick={() => download(latest)}>
              <Download className="mr-2 h-4 w-4" /> Download APK ({latest.version})
            </Button>
            <p className="text-xs text-slate-500">
              On Android, open the downloaded file to install. You may need to allow "Install from
              unknown sources" the first time.
            </p>
          </CardContent>
        </Card>
      ) : (
        <p className="mb-6 text-sm text-slate-500" data-testid="apk-none">No app build has been published yet.</p>
      )}

      {isAdmin && (
        <Card className="mb-6 border-brand-line shadow-none" data-testid="apk-upload-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Upload className="h-4 w-4 text-slate-400" /> Publish a new build
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Version number</Label>
                <Input className="mt-1.5 min-h-[44px]" placeholder="e.g. 1.4.0" data-testid="apk-version-input"
                  value={version} onChange={(e) => setVersion(e.target.value)} />
              </div>
              <div>
                <Label>APK file</Label>
                <Input ref={fileRef} type="file" accept=".apk,application/vnd.android.package-archive"
                  className="mt-1.5 min-h-[44px]" data-testid="apk-file-input" />
              </div>
            </div>
            <div>
              <Label>Release note</Label>
              <Textarea rows={3} className="mt-1.5" placeholder="What's new in this build…"
                data-testid="apk-note-input" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <Button className="min-h-[44px]" data-testid="apk-publish-btn" onClick={upload} disabled={busy}>
              {busy ? "Uploading…" : "Publish build"}
            </Button>
          </CardContent>
        </Card>
      )}

      {isAdmin && releases.length > 0 && (
        <Card className="border-brand-line shadow-none" data-testid="apk-releases-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Release history</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {releases.map((r) => (
              <div key={r.id} data-testid={`apk-release-${r.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-brand-line p-3 text-sm">
                <div>
                  <p className="font-semibold text-brand-ink">v{r.version} <span className="ml-1 text-xs font-normal text-slate-400">{fmtSize(r.size)}</span></p>
                  {r.release_note && <p className="text-xs text-slate-500">{r.release_note}</p>}
                  <p className="text-xs text-slate-400">{(r.created_at || "").slice(0, 10)}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => download(r)}>
                  <Download className="mr-1.5 h-3.5 w-3.5" /> APK
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function AppUpdateBanner() {
  const { user } = useAuth();
  const [rel, setRel] = useState(null);
  useEffect(() => {
    if (!APK_ROLES.includes(user?.role)) return;
    api.get("/apk/latest").then(({ data }) => {
      if (data && data.version && localStorage.getItem("apk_seen_version") !== data.version) setRel(data);
    }).catch(() => {});
  }, [user]);
  if (!rel || !APK_ROLES.includes(user?.role)) return null;
  const get = () => { localStorage.setItem("apk_seen_version", rel.version); window.location.href = `${API}${rel.download_url}`; };
  const dismiss = () => { localStorage.setItem("apk_seen_version", rel.version); setRel(null); };
  return (
    <div data-testid="apk-update-banner" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand bg-brand-tint/40 px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-brand-ink">
        <Smartphone className="h-4 w-4 text-brand" />
        <span><strong>New app version {rel.version}</strong> is available.{rel.release_note ? ` ${rel.release_note}` : ""}</span>
      </div>
      <div className="flex gap-2">
        <Button size="sm" data-testid="apk-banner-download" onClick={get}>
          <Download className="mr-1.5 h-3.5 w-3.5" /> Download
        </Button>
        <Button size="sm" variant="ghost" data-testid="apk-banner-dismiss" onClick={dismiss}>Not now</Button>
      </div>
    </div>
  );
}
